import classNames from 'classnames';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { FunctionComponent } from 'react';

import Locator from '../../../../../shared/Locator';
import ProjectsModule from '../../../../redux/projects';
import { getProjectBaseURLFromParams, nuc } from '../../../../utils';
import Icon, { icons } from '../../../generic/Icon';
import RouterLink from '../../../generic/Link/RouterLink';
import Tooltip from '../../../generic/Tooltip';
import ProjectTypeIcon from '../../generic/ProjectTypeIcon';
import { getLatestRevision } from '../helper';
import type { ProjectEntry } from '../../../../redux/projects/store';
import type { ProjectGroupReleaseProject, Revision } from '../../../../types';

import ProjectReleaseBranchSelect from './ProjectReleaseBranchSelect';
import ProjectReleaseRevisionSelect from './ProjectReleaseRevisionSelect';

// This is not an extensive list of all of the fields, just the minimum for typing the ProjectLink.
type EditableRowProps = {
  editMode?: boolean;
  projectUrl: string;
  className: string;
};

type ProjectLinkProps = {
  projectUrl: string;
  className: string;
};

const ProjectLink: FunctionComponent<ProjectLinkProps> = ({ children, projectUrl, className }) => (
  <RouterLink
    to={projectUrl}
    className={classNames('project-table__row__link', className)}
  >
    {children}
  </RouterLink>
);

const EditableColumn: FunctionComponent<EditableRowProps> = ({ children, editMode, projectUrl, className }) =>
  editMode ? (
    <td className={className}>{children}</td>
  ) : (
    <ProjectLink className={className} projectUrl={projectUrl}>
      {children}
    </ProjectLink>
  );

type ProjectRowProps = {
  showRemoveButton?: boolean;
  editMode?: boolean;
  project: ProjectGroupReleaseProject;
  onSelect: () => void;
  onChangeBranch: (branch: string) => void;
  onChangeRevision: (revision: Revision) => void;
};

const ProjectReleaseRow = ({
  showRemoveButton,
  project: row,
  onSelect,
  editMode,
  onChangeBranch,
  onChangeRevision,
}: ProjectRowProps) => {
  const allProjects: Record<string, ProjectEntry> = useSelector(ProjectsModule.selectors.getProjects);

  const projectRevisionColumnClassName = classNames({
    'project-revision': editMode,
    'project-revision--read-only': !editMode,
  });
  const projectBranchColumnClassName = classNames({
    'project-branch': editMode,
    'project-branch--read-only': !editMode,
  });
    // If we've loaded the Revision, we can link directly to it. If it hasn't loaded yet, we just open the project.
  const projectUrl = row.revision
    ? getProjectBaseURLFromParams({
      project_id: row.projectId,
      reference_type: 'branch',
      reference: row.branch,
      revision_id: row?.revision?.loc?.revision ?? Locator.parse(row.revisionId).revision ?? '',
    })
    : `/projects/${nuc(row.projectId)}`;

  return (
    <tr>
      <ProjectLink className="project-title-col" projectUrl={projectUrl}>
        <Tooltip tooltip={row.project.title} id="project-row-project-name">
          <span className="project-title">
            <b>{row.project.title}</b>
          </span>
        </Tooltip>
        {!row.project.public && (
          <Tooltip tooltip="Private" id="project-row-private-tooltip">
            <span className="project-table__row__icon">
              <Icon icon={icons.lock} />
            </span>
          </Tooltip>
        )}
        <ProjectTypeIcon project={row.project} />
      </ProjectLink>
      <EditableColumn editMode={editMode} className={projectBranchColumnClassName} projectUrl={projectUrl}>
        <ProjectReleaseBranchSelect
          branch={row.branch}
          locator={row.project.locator}
          isEditable={editMode}
          onChangeBranch={onChangeBranch}
        />
      </EditableColumn>
      <EditableColumn editMode={editMode} className={projectRevisionColumnClassName} projectUrl={projectUrl}>
        <ProjectReleaseRevisionSelect
          branch={row.branch}
          revision={row.revision}
          locator={row.project.locator}
          latestRevision={getLatestRevision(allProjects, row)}
          isEditable={editMode}
          onChangeRevision={onChangeRevision}
        />
      </EditableColumn>
      <ProjectLink className="project-modified" projectUrl={projectUrl}>
        {moment(row.revision?.dependency_cache_updated ?? row.revision?.updatedAt).fromNow()}
      </ProjectLink>
      {showRemoveButton && (
        <td className="remove-column">
          <Button
            block
            bsStyle="link"
            onClick={onSelect}
          >
            <Icon icon={icons.times} />
          </Button>
        </td>
      )}
    </tr>
  );
};

export default ProjectReleaseRow;
